/**
 * Wallet address branded type and Zod schema
 *
 * WalletAddress follows ARCH-23 conventions:
 * - Stored as lowercase hex
 * - Compared via toLowerCase()
 * - Displayed checksummed truncated
 * - Validated with Zod regex (0x prefix, 42 chars, lowercase hex)
 */

import { z } from "zod";

import type { Brand } from "./domain/branded";

export type WalletAddress = Brand<string, "WalletAddress">;

/**
 * Zod schema for validating wallet addresses.
 * Accepts lowercase hex, 0x prefix, exactly 42 characters.
 */
export const WalletAddressSchema = z
	.string()
	.regex(/^0x[0-9a-f]{40}$/, "Must be a valid lowercase hex wallet address with 0x prefix");

/**
 * Factory function for creating a branded WalletAddress.
 * Does NOT validate — use WalletAddressSchema.parse() for validation.
 */
export function walletAddress(address: string): WalletAddress {
	return address as WalletAddress;
}

/**
 * Type guard for runtime validation of wallet addresses.
 * Checks lowercase hex format, 0x prefix, 42 chars.
 */
export function isWalletAddress(value: unknown): value is WalletAddress {
	return typeof value === "string" && /^0x[0-9a-f]{40}$/.test(value);
}
